import { useDimensions } from '@/hooks/useDimensions';
import { getAudioAllTracksV2 } from '@/service/audioList';
import { AudioListResponse, AudioListV2Request, QueryType } from '@/service/interface';
import { AudioDto, SortOrderType } from '@/service/type';
import { useAudioListStore, useIsPlayAudioInview } from '@/state/audio-list-store';
import { useSongDetailDrawer } from '@/state/drawer-store';
import { useRequest } from 'ahooks';
import { useLocation, useParams } from 'react-router-dom';
import useGeneratorStore, { GeneratorStore } from '@/state/generator-store';
import React, { useEffect, useRef, useState } from 'react';
import AudioListItem from './AudioItem';
import { AudioListHeader } from './AudioListHeader';
import { useAudioDetailStore } from '@/state/audio-detail-store';
import { useRefreshData } from '@/hooks/useRefreshData';
import { throttle } from 'lodash-es';
import { cn, LOCAL_STORAGE_ENUM } from '@/utils';
import { useAudioPlayerStore } from '@/state/audio-player-store';
import { useIsScrollingDown } from '@/hooks/useScrollingDown';
import useInViewport from '@/hooks/useInviewPort';
import { useInstructionShow, useLinksDetailShow } from '@/state/show-store';
import { useCatalogOverviewStore } from '@/state/catalog-store';
import AnimateFavIcon from '@jenmusic/animate-favicon';
import favIcon1 from '@/assets/favIcons/1.png';
import favIcon2 from '@/assets/favIcons/2.png';
import favIcon3 from '@/assets/favIcons/3.png';
import favIcon4 from '@/assets/favIcons/4.png';
import favIcon5 from '@/assets/favIcons/5.png';
import favIcon6 from '@/assets/favIcons/6.png';
import favIcon7 from '@/assets/favIcons/7.png';

interface AudioListProps {
  onlyShowList?: boolean;
  actionShowConfig?: {
    showFolder?: boolean;
    showTime?: boolean;
    showLike?: boolean;
    showEllipsisAction?: boolean;
  };
}

export const PAGE_SIZE = 20;
export const INIT_PAGE = 0;

export const getFetchAudioByPath = (path: string, query: AudioListV2Request) => {
  const isFolderPath = path.startsWith('/folder');
  if (isFolderPath) {
    return () => getAudioAllTracksV2(query);
  }
  const queryType = path.split('/')[1] as QueryType;
  return () => getAudioAllTracksV2({ ...query, queryType });
};

const AudioList: React.FC<AudioListProps> = ({ onlyShowList = false, actionShowConfig }) => {
  const viewportRef = useRef<HTMLDivElement | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);

  const [inViewport] = useInViewport(viewportRef);

  const { setIsPlayAudioInview } = useIsPlayAudioInview();

  const { isMobileOrPad } = useDimensions();

  const [page, setPage] = useState<number>(INIT_PAGE);

  const [order, setOrder] = useState<SortOrderType>('LF');

  const { setIsDrawerShow } = useSongDetailDrawer();

  const [ifScrolledSomeDistance, setIfScrolledSomeDistance] = useState(false);

  const [name, setName] = useState('');

  const location = useLocation();

  const pathname = location.pathname;

  const { folderId } = useParams<{ folderId: string }>();

  const { audioList, selectedInfo, setSelectedInfo, setAudioList, setCount, setAudioItem, updateAudioInList } = useAudioListStore(
    (state) => ({
      audioList: state.audioList,
      selectedInfo: state.selectedInfo,
      setSelectedInfo: state.setSelectedInfo,
      setAudioList: state.setAudioList,
      setCount: state.setCount,
      setAudioItem: state.setAudioItem,
      updateAudioInList: state.updateAudioInList,
    }),
  );

  const { refreshCatalogOverview } = useRefreshData();

  const { catalogOverviewStore } = useCatalogOverviewStore();

  const isNewUser = Object.values(catalogOverviewStore).every((catalogNum) => catalogNum === 0);

  const audioPlayerStore = useAudioPlayerStore();

  useEffect(() => {
    const handleSpaceKeyPress = (e: KeyboardEvent) => {
      const isEditing = e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement;
      const SPACE_KEY_CODE = 32;
      if (e.keyCode === SPACE_KEY_CODE && !isEditing) {
        audioPlayerStore.playOrPause(selectedInfo, audioList);
        e.preventDefault();
      }
    };
    if (selectedInfo) {
      window.addEventListener('keydown', handleSpaceKeyPress);
    }
    return () => {
      window.removeEventListener('keydown', handleSpaceKeyPress);
    };
  }, [selectedInfo]);

  const { updateAudioDetail } = useAudioDetailStore((state) => ({
    updateAudioDetail: state.updateAudioDetail,
  }));

  const { isGeneratorDrawerShow, resetGeneratorResult } = useGeneratorStore((state) => ({
    isGeneratorDrawerShow: state.isGeneratorDrawerShow,
    resetGeneratorResult: state.resetGeneratorResult,
  }));
  const { setIsShow: setIsInstructionShow, isShow: isShowInstruction } = useInstructionShow();

  const { subscribe } = useGeneratorStore;

  const [animateFavIconStart, setAnimateFavIconStart] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribe((state: GeneratorStore, prevState: GeneratorStore) => {
      if (state.generatorActionTrigger && !prevState.generatorActionTrigger) {
        if (location.pathname === '/') {
          if (page !== INIT_PAGE) {
            setPage(INIT_PAGE);
          } else {
            runFetchAudioList();
          }
          if (scrollRef?.current) {
            scrollRef.current.scrollTop = 0;
          }
        }
        if (isNewUser) {
          setIsInstructionShow(true);
        }
        setAnimateFavIconStart(true);

        refreshCatalogOverview();

        resetGeneratorResult();
      }
    });
    return () => unsubscribe();
  }, [subscribe, resetGeneratorResult, page, location, isNewUser]);

  const isScrollingDown = useIsScrollingDown(scrollRef, 100, 200);

  const {
    data: fetchedAudioListData,
    run: runFetchAudioList,
    loading,
  } = useRequest<AudioListResponse, any[]>(
    getFetchAudioByPath(pathname, {
      page,
      pageSize: PAGE_SIZE,
      sortOrder: order,
      name: name,
      folderId: folderId ? +folderId : undefined,
    }),
    {
      refreshDeps: [
        pathname,
        JSON.stringify({
          page,
          pageSize: PAGE_SIZE,
          sortOrder: order,
          name: name,
          folderId,
        }),
      ],
      onSuccess(data) {
        const { data: fetchedAudioList, count } = data;
        if (page !== INIT_PAGE) {
          setAudioList([...audioList, ...(fetchedAudioList ?? [])]);
        } else {
          setAudioList([...(fetchedAudioList ?? [])]);
          setSelectedInfo((fetchedAudioList ?? [])[0], isShowInstruction);
          setCount(count ?? 0);
        }
      },
    },
  );

  useEffect(() => {
    setIsPlayAudioInview(inViewport);
  }, [inViewport]);

  useEffect(() => {
    setAudioList([]);
    setOrder('LF');
    setName('');
    setPage(INIT_PAGE);
  }, [location.pathname]);

  // reset page when name or order changed
  useEffect(() => {
    setPage(INIT_PAGE);
  }, [name, order]);

  const { setIsShow: setIsLinksDetailShow } = useLinksDetailShow();

  const onSelect = (audioInfo: AudioDto) => {
    if (isGeneratorDrawerShow) {
      return;
    }
    if (audioInfo.is_deleted || audioInfo.hasError) {
      return;
    }
    if (isMobileOrPad) {
      setIsDrawerShow(true);
    }
    setIsLinksDetailShow(false);
    setSelectedInfo(audioInfo);
  };

  useEffect(() => {
    const scrollElement = scrollRef.current;

    const handleScroll = throttle(() => {
      if (!scrollElement) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollElement;
      setIfScrolledSomeDistance(scrollTop >= 150);

      const BOTTOM_DISTANCE_TO_LOAD = 1000;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - BOTTOM_DISTANCE_TO_LOAD;
      const isLastAudioAvailable = audioList[audioList.length - 1]?.id;
      const isMoreDataAvailable = audioList.length !== fetchedAudioListData?.count;

      if (isAtBottom && isLastAudioAvailable && isMoreDataAvailable && !loading) {
        setPage((page) => page + 1);
      }
    }, 1000);

    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [audioList.length, loading]);

  const renderContent = () => {
    if (audioList.length) {
      return audioList.map((audioInfo) => (
        <AudioListItem
          actionShowConfig={actionShowConfig}
          audioList={audioList}
          setAudioItem={setAudioItem}
          updateAudioDetail={updateAudioDetail}
          viewportRef={viewportRef}
          key={`${audioInfo.id}`}
          audioInfo={audioInfo}
          selectId={selectedInfo?.id}
          onSelect={onSelect}
          updateAudioInList={updateAudioInList}
        />
      ));
    }

    if (loading) {
      return null;
    }
    return <Empty />;
  };
  if (onlyShowList) {
    return (
      <div
        className={cn('h-full track_list_box relative z-[1] overflow-y-auto  px-jen-spacing-4 lg:px-jen-spacing-24 lg:pb-0')}
        ref={scrollRef}
      >
        {renderContent()}
      </div>
    );
  }
  return (
    <div className="h-full grid grid-rows-[auto_minmax(0,1fr)] relative bg-jen-background-secondary">
      <AudioListHeader
        isHideOnMobile={isScrollingDown}
        order={order}
        setOrder={setOrder}
        setName={setName}
        ifScrolledSomeDistance={ifScrolledSomeDistance}
      />
      <div
        className={cn(
          'track_list_box relative z-[1] overflow-y-auto  pt-[140px] lg:pt-[96px] px-jen-spacing-4 lg:px-jen-spacing-24 lg:pb-0',
          {
            'pb-[50px]': !!audioPlayerStore.playAudioInfo,
          },
        )}
        ref={scrollRef}
      >
        {renderContent()}
      </div>
      <AnimateFavIcon
        sequences={[favIcon1, favIcon2, favIcon3, favIcon4, favIcon5, favIcon6, favIcon7]}
        start={animateFavIconStart}
        loop={2}
        endCallback={() => setAnimateFavIconStart(false)}
      />
    </div>
  );
};

export const Empty = () => {
  const { setIsGeneratorDrawerShow } = useGeneratorStore((state) => ({
    setIsGeneratorDrawerShow: state.setIsGeneratorDrawerShow,
  }));
  const { pathname } = useLocation();
  return (
    <div className="flex flex-col justify-center items-center h-full text-jen-size-heading-01 text-jen-content-tertiary gap-[24px]">
      <div>No Tracks</div>
      {pathname === '/' && (
        <div
          onClick={() => setIsGeneratorDrawerShow(true)}
          className="cursor-pointer px-[24px] py-[12px] text-jen-size-interactive-primary text-jen-content-primary-contrast bg-jen-interactive-primary rounded-[8px]"
        >
          Create something new
        </div>
      )}
    </div>
  );
};

export default AudioList;
